/* eslint-disable max-lines-per-function */
import { CloseCircleOutlined } from '@ant-design/icons'
import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import { AxiosResponsePromise } from '@seiue/axios'
import { getTheme } from '@seiue/theme'
import { Button, ColumnsType, Ellipsis, ButtonIcon } from '@seiue/ui'
import { ConfigProvider, Table, TableProps as AntdTableProps } from 'antd'
import { TableRowSelection } from 'antd/es/table/interface'
import { omit } from 'lodash'
import { Expand } from 'packages/sdks-next/types'
import { useQueryApi } from 'packages/sdks-next/utils'
import { THEME_COLOR } from 'packages/theme'
import { AnyType } from 'packages/types'
import { fireKeyEvent } from 'packages/utils'
import React, {
  useState,
  useEffect,
  Key,
  useRef,
  forwardRef,
  useImperativeHandle,
  FC,
} from 'react'
import styled from 'styled-components'

import { Empty } from '../Empty'

import { SearchModal } from './SearchModal'
import {
  FormConfig,
  IMap,
  IPageInfo,
  IParams,
  ISearchParams,
  TableQuery,
} from './types'
import { valueFormatToText } from './utils'

const searchValueToText = (value: AnyType) => {
  return valueFormatToText(value)
}

export type DataTableProps = {
  columns: ColumnsType<AnyType>
  api: (q: TableQuery) => AxiosResponsePromise<Expand<AnyType, AnyType>[]>
  apiName: string
  extraApiDependence?: any[]
  isPagination?: boolean
  searchFormConfig?: FormConfig
  showSearchForm?: boolean
  formatData?: (data: AnyType) => AnyType
  formatParams?: (params: IParams) => IParams
  selectedRowKeys?: Key[]
  onSelectedRowKeysChange?: (selectedKeys: React.Key[]) => void
  rowKey?: string
  showSelectedAll?: boolean
  headerLeft?: React.ReactNode
  headerRight?: React.ReactNode
  headerTitle?: React.ReactNode
  scroll?: {
    x?: number | true | string
    y?: number | string
  }
  showSelectedRows?: boolean
  showCount?: boolean
  expandable?: AntdTableProps<AnyType>['expandable']
  loading?: boolean
  /**
   * 自定义获取全选数据 keys 集合
   */
  fetchAllKeys?: () => Promise<Key[]>
}

export type TableRef = {
  reload: () => void
  clearSearch: () => void
}

/**
 * 筛选表格
 *
 * @param param - 参数
 * @param param.columns - 表头【表头的key要和筛选表单中的name对应】
 *  问： columns的key为啥要和筛选表单的name相同呢？
 *  答： - 只有和这里的key和筛选表单Form.Item的name属性相同时，
 *        才可以实现点击表头打开筛选弹窗滚动到视图内并聚焦
 * @param param.api - 获取表格数据API
 * @param param.apiName - 用于API数据更新 例如 api 为 questionnaireApi.queryQuestionnaireTaskReflections， apiName 为 Questionnaire.queryQuestionnaireTaskReflections
 * @param param.isPagination - 是否分页
 * @param param.searchFormConfig - 筛选表单配置
 * @param param.showSearchForm - 是否显示筛选
 * @param param.formatData - 对API获取的原始数据格式化【API返回的数据已经格式化则可以不传】
 * @param param.formatParams - 将筛选表单提交的数据格式化为API query格式
 * @param param.selectedRowKeys - 表格多选操作时选中的key组成的数组【需要多选操作时传入】
 * @param param.onSelectedRowKeysChange - 表格多选选中数据改变时的回调【需要多选操作时传入】
 * @param param.rowKey - 表格rowKey【默认为id】
 * @param param.showSelectedAll - 是否展示选择全部
 * @param param.headerLeft - React.ReactNode
 * @param param.headerRight - React.ReactNode
 * @param param.scroll - 表格滚动配置
 * @param param.showSelectedRows - 是否显示只选数据
 * @param param.showCount - 是否显示数量
 * @returns React.ReactNode
 */
export const DataTable = forwardRef<TableRef, DataTableProps>(
  (
    {
      columns,
      api,
      apiName,
      extraApiDependence = [],
      formatData,
      formatParams,
      isPagination = true,
      searchFormConfig,
      showSearchForm,
      selectedRowKeys,
      onSelectedRowKeysChange,
      rowKey,
      showSelectedAll,
      headerLeft,
      headerRight,
      scroll,
      showSelectedRows,
      showCount,
      expandable,
      loading: loadingProp,
      fetchAllKeys,
      headerTitle,
    },
    ref
  ) => {
    const { background, text } = getTheme(THEME_COLOR)
    const [visible, setVisible] = useState<boolean>(false)
    const [nameToLabel, setNameToLabel] = useState<IMap>()
    const [showValueMap, setShowValueMap] = useState<IMap>()
    // 调用筛选API时传递的参数
    const [params, setParams] = useState<ISearchParams>({})
    // 用于展示筛选条件Tag以及为搜索表单设置Value的参数
    const [searchParams, setSearchParams] = useState<ISearchParams>({})
    const [pageInfo, setPageInfo] = useState<IPageInfo>({
      page: 1,
      perPage: 20,
    })
    const [allKeys, setAllKeys] = useState<string[]>([])
    const [isSelectedAll, setIsSelectedAll] = useState<boolean>(false)
    const timer = useRef<ReturnType<typeof setTimeout>>()

    const {
      data,
      pagination,
      loading: loadingApi,
      reload,
    } = useQueryApi(
      api,
      apiName,
      {
        getQueryKey: (queryKey) => [...queryKey, ...extraApiDependence],
      },
      Object.assign(
        {},
        params,
        isPagination ? pageInfo : {},
        showSelectedRows &&
          selectedRowKeys !== undefined &&
          selectedRowKeys?.length > 0
          ? { idIn: selectedRowKeys?.join(',') }
          : {}
      )
    )

    useEffect(() => {
      showSelectedAll && selectAllRow(true)
      return () => {
        timer.current && clearTimeout(timer.current)
      }
    }, [])

    useEffect(() => {
      getDataShowMap()
    }, [searchFormConfig])

    const getDataShowMap = () => {
      const mapObj: IMap = {}
      const valueMapObj: IMap = {}
      if (!searchFormConfig) return
      Object.keys(searchFormConfig).forEach((key) => {
        searchFormConfig[key].forEach((item) => {
          mapObj[item.name] = item.label
          const opts = item.otherConfig?.options
          if (opts) {
            opts.map((opt) => {
              valueMapObj[opt.value] = opt.label
            })
          }
        })
      })
      setNameToLabel(mapObj)
      setShowValueMap(valueMapObj)
    }

    const searchValueToLabel = (key: string, value: AnyType) => {
      if (showValueMap && showValueMap[value]) {
        return showValueMap[value]
      }
      if (Array.isArray(value)) {
        return value.map((v) => {
          return (showValueMap && showValueMap[v]) || v
        })
      }
      return searchParams[key]
    }

    const formatSearchParams = () => {
      return Object.keys(searchParams).map((key) => {
        return {
          label: (nameToLabel && nameToLabel[key]) || key,
          name: key,
          value: searchValueToLabel(key, searchParams[key]),
        }
      })
    }

    const getCols = () => {
      const finalCols = columns.map((col) => {
        return {
          ...col,
          title: <Ellipsis text={col.title} />,
          render: (v: unknown, r: unknown, i: number) =>
            col.render ? col.render(v, r, i) : <Ellipsis text={v} />,
          onHeaderCell: (record: AnyType) => {
            return {
              onClick: async (e: any) => {
                const target = e?.target as HTMLDivElement | null
                // 判断是否是点击排序元素，如果是，则不打开筛选抽屉
                const isSortElement = target
                  ?.getAttribute('class')
                  ?.includes('ant-table-column-sorters')

                if (showSearchForm === false || isSortElement) {
                  return
                }
                let eleId = record.key
                const ele = document.getElementById(eleId)
                if (ele) {
                  await setVisible(true)
                  ele.scrollIntoView({ block: 'center', behavior: 'smooth' })
                  timer.current && clearTimeout(timer.current)
                  timer.current = setTimeout(() => {
                    ele.focus()
                    fireKeyEvent(ele, 'keydown', 32)
                  }, 300)
                }
              },
            }
          },
        }
      })
      return finalCols
    }

    const closeSearchParamTag = (name: string) => {
      const newParams = omit(searchParams, [name])
      setPageInfo({
        page: 1,
        perPage: pageInfo.perPage,
      })
      setSearchParams(newParams)
      setParams(formatParams ? formatParams(newParams) : newParams)
    }

    const getSearchParamsTag = () => {
      return formatSearchParams().map((param) => {
        return (
          <SearchParamsTag
            key={param.name}
            label={param.label}
            value={param.value}
            onClose={() => closeSearchParamTag(param.name)}
          />
        )
      })
    }

    const clearSearchParams = () => {
      setPageInfo({
        page: 1,
        perPage: pageInfo.perPage,
      })
      setSearchParams({})
      setParams({})
    }

    const onSearch = (newParams: IParams) => {
      setPageInfo({
        page: 1,
        perPage: pageInfo.perPage,
      })
      setSearchParams(newParams)
      setParams(formatParams ? formatParams(newParams) : newParams)
      setVisible(false)
    }

    const getSearchParamWrapper = () => {
      if (searchParams && formatSearchParams().length > 0) {
        return (
          <SearchParamWrapper>
            <ResultTotalWrapper>
              {`共${pagination?.totalCount || 0}条`}
            </ResultTotalWrapper>
            <TagWrapper>{getSearchParamsTag()}</TagWrapper>
            <ClearParamsDiv onClick={clearSearchParams}>
              <CloseCircleOutlined />
              <span>&nbsp;清空</span>
            </ClearParamsDiv>
          </SearchParamWrapper>
        )
      }
      return null
    }

    const selectAllRow = async (isInit = false) => {
      if (fetchAllKeys) {
        const res = await fetchAllKeys()
        setAllKeys(res.map(String))
        if (!isInit) {
          setIsSelectedAll(true)
          onSelectedRowKeysChange && onSelectedRowKeysChange(res)
        }
        return
      }

      const { data } = await api({
        ...params,
        page: undefined,
        perPage: undefined,
        expand: undefined,
        tryExpand: undefined,
        paginated: 0,
        fields: rowKey || 'id',
      })

      const keys = data.map((d: AnyType) => d[rowKey || 'id'])

      setAllKeys(keys)
      if (!isInit) {
        setIsSelectedAll(true)
        onSelectedRowKeysChange && onSelectedRowKeysChange(keys)
      }
    }

    const cancelSelectedAll = () => {
      setIsSelectedAll(false)
      onSelectedRowKeysChange && onSelectedRowKeysChange([])
    }

    const rowSelection: TableRowSelection<AnyType> = {
      fixed: true,
      type: 'checkbox',
      selectedRowKeys,
      onSelect: (record, selected) => {
        const key = record[rowKey || 'id']
        const preSelectedRowKeys: Key[] = selectedRowKeys || []
        if (selected) {
          const newKeys = [...preSelectedRowKeys, key]
          onSelectedRowKeysChange && onSelectedRowKeysChange(newKeys)
          setIsSelectedAll(newKeys.length === allKeys.length)
        } else {
          const newKeys = preSelectedRowKeys.filter((i: Key) => i !== key)
          onSelectedRowKeysChange && onSelectedRowKeysChange(newKeys)
          setIsSelectedAll(false)
        }
      },
      onSelectAll: async (
        selected: boolean,
        selectedRows: AnyType,
        changeRows: AnyType
      ) => {
        const preSelectedRowKeys: Key[] = selectedRowKeys || []
        if (selected) {
          const newKeys = changeRows.map((i: AnyType) =>
            rowKey ? i[rowKey] : i.id
          )

          onSelectedRowKeysChange &&
            onSelectedRowKeysChange([...preSelectedRowKeys, ...newKeys])
        } else {
          const cancelKeys = changeRows.map((i: AnyType) => i[rowKey || 'id'])
          const newKeys = preSelectedRowKeys.filter(
            (i: AnyType) => !cancelKeys.includes(i)
          )
          onSelectedRowKeysChange && onSelectedRowKeysChange(newKeys)
          setIsSelectedAll(false)
        }
      },
    }

    useImperativeHandle(ref, () => ({
      reload,
      clearSearch: () => {
        clearSearchParams()
      },
    }))

    const loading = loadingProp || loadingApi

    // if (loading) return <State.Loading />
    // if (columns.length === 0) return <Empty />

    return (
      <>
        <BtnsWrapper>
          <ChildrenWrapper style={{ marginBottom: showSelectedAll ? 20 : 8 }}>
            {headerTitle}
            {showSelectedAll ? (
              <TableSelect>
                <SelectedTotal>
                  已选择&nbsp;{selectedRowKeys?.length || 0}/
                  {pagination?.totalCount || 0}&nbsp;条
                </SelectedTotal>
                {!(
                  selectedRowKeys?.length &&
                  pagination?.totalCount &&
                  selectedRowKeys?.length === pagination?.totalCount
                ) && (
                  <SelectedAll onClick={() => selectAllRow()}>
                    选择全部
                  </SelectedAll>
                )}
                {selectedRowKeys && selectedRowKeys?.length > 0 && (
                  <CancelSelected onClick={cancelSelectedAll}>
                    取消选择
                  </CancelSelected>
                )}
              </TableSelect>
            ) : showCount ? (
              <ResultTotalWrapper>
                {`共 ${pagination?.totalCount || 0} 条数据`}
              </ResultTotalWrapper>
            ) : null}
            <HeaderLeftWrapper>{headerLeft}</HeaderLeftWrapper>
            <HeaderRightWrapper>{headerRight}</HeaderRightWrapper>
          </ChildrenWrapper>
          {showSearchForm === false ? null : (
            <SearchButton
              type="special"
              icon={faSearch}
              onClick={() => setVisible(true)}
            >
              筛选
            </SearchButton>
          )}
        </BtnsWrapper>
        {getSearchParamWrapper()}
        <SearchModal
          visible={visible}
          searchFormConfig={searchFormConfig || {}}
          searchParams={searchParams}
          onCancel={() => setVisible(false)}
          onOk={onSearch}
        />
        <ConfigProvider renderEmpty={() => !loading && Empty}>
          <StyledTable
            loading={loading}
            columns={getCols()}
            dataSource={formatData ? formatData(data || []) : data || []}
            size="large"
            rowKey={rowKey || 'id'}
            rowSelection={selectedRowKeys ? rowSelection : undefined}
            pagination={
              isPagination && pagination?.totalCount
                ? {
                    pageSize: pagination?.perPage || pageInfo.perPage,
                    current: pagination?.currentPage || pageInfo.page,
                    total: pagination?.totalCount || 0,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total: number) => {
                      return `共${total}条`
                    },
                    onChange: (page: number, perPage: number) => {
                      setPageInfo({ page, perPage })
                    },
                  }
                : false
            }
            scroll={
              searchParams && formatSearchParams().length > 0
                ? {
                    x: scroll?.x || '1300',
                    y: scroll?.y
                      ? `calc(${scroll.y} - 54px)`
                      : 'calc(100vh - 374px)',
                  }
                : scroll || { x: '1300', y: 'calc(100vh - 320px)' }
            }
            expandable={expandable}
          />
        </ConfigProvider>
      </>
    )
  }
)

const BtnsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const ChildrenWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 10;
  gap: 16px;
`

const HeaderLeftWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`

const HeaderRightWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-grow: 10;
`

const TableSelect = styled.div`
  height: 100%;
  flex-shrink: 0;
  font-size: 14px;
  display: flex;
  align-items: flex-end;
`

const SelectedTotal = styled.span`
  color: ${(props) => props.theme.text._2};
`

const SelectedAll = styled.span`
  margin-left: 12px;
  color: ${(props) => props.theme.brand._1};
  cursor: pointer;
`

const CancelSelected = styled.span`
  margin-left: 12px;
  color: ${(props) => props.theme.text._3};
  cursor: pointer;
`

const SearchButton = styled(Button)`
  &&& {
    margin-right: 0;
    border: 1px solid ${(props) => props.theme.brand._1};
    color: ${(props) => props.theme.brand._1} !important;
    border-color: ${(props) => props.theme.brand._1} !important;
  }
`

const SearchParamWrapper = styled.div`
  width: 100%;
  padding: 0 20px;
  background-color: ${(props) => props.theme.background._3};
  min-height: 40px;
  margin-bottom: 12px;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
`

const ResultTotalWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 14px;
`

const TagWrapper = styled.div`
  padding: 9px 12px;
  flex-grow: 10;
  line-height: 30px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const SearchParamsTag: FC<{
  label: string
  value: string
  onClose?: () => void
}> = ({ label, value, onClose }) => {
  return (
    <SearchTagWrapper>
      <ParamKey>{label}：</ParamKey>
      <SearchValue>{searchValueToText(value)}</SearchValue>
      <ButtonIcon
        icon="Close"
        onClick={onClose}
        size="small"
        style={{ marginLeft: 4 }}
      />
    </SearchTagWrapper>
  )
}

const SearchTagWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
  background-color: #fff;
  border-radius: 4px;
  padding: 0 4px 0 8px;
`

const ParamKey = styled.span`
  color: ${(props) => props.theme.text._2};
  line-height: 24px;
`

const ClearParamsDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 60px;
  text-align: right;
  font-size: 14px;
  line-height: 22px;
  color: ${(props) => props.theme.text._3};
  cursor: pointer;
`

const SearchValue = styled.span`
  color: ${(props) => props.theme.brand._1};
  line-height: 24px;
`

const StyledTable = styled(Table)`
  &&& {
    // thead样式调整
    .ant-table-thead > tr > th {
      height: 40px;
      padding: 0 0 0 16px;
      color: ${(props) => props.theme.text._1};
      font-weight: 500;
      background-color: ${(props) => props.theme.background._4};
      &:first-child {
        padding: 10px;
      }
    }
    // 去除表头分割线
    .ant-table-thead
      > tr
      > th:not(:last-child):not(.ant-table-selection-column):not(
        .ant-table-row-expand-icon-cell
      ):not([colspan])::before {
      display: none;
    }

    // tbody样式调整
    .ant-table-tbody > tr > td {
      height: 40px;
      padding: 0 0 0 16px;
      border-color: ${(props) => props.theme.border.greyNormal};
      color: ${(props) => props.theme.text._1};
      font-weight: 400;
      &:first-child {
        padding: 10px;
      }
    }

    // 字段数据为空时显示-
    .ant-table-cell:empty:after,
    .ant-table-cell > span:empty:after,
    .ant-table-cell > div:empty:after {
      content: '-';
    }

    // 选中行时的样式调整
    .ant-table tbody > tr.ant-table-row-selected > td {
      background-color: ${(props) => props.theme.background._5};
    }

    .ant-checkbox-checked .ant-checkbox-inner {
      background-color: ${(props) => props.theme.brand._1};
    }
    .ant-checkbox:hover {
      .ant-checkbox-inner {
        border-color: ${(props) => props.theme.brand._1};
      }
    }
    .ant-checkbox-indeterminate .ant-checkbox-inner::after {
      background-color: ${(props) => props.theme.brand._1};
    }

    // 分页器颜色修改
    .ant-table-pagination.ant-pagination {
      display: flex;
      align-items: center;
      font-size: 14px;
      margin: 14px 0 0 0;
    }
    .ant-select-selector {
      width: 120px;
      height: 32px;
      padding: 5px 12px;
      border: none;
      border-radius: 8px;
      font-weight: 400;
      background-color: #f0f5f5;
      & .ant-select-selection-item {
        font-size: 14px;
        line-height: 22px;
        color: ${(props) => props.theme.text._1};
        font-weight: 400;
      }
    }
    .ant-select-item-option-selected .ant-select-item-option-content {
      color: ${(props) => props.theme.brand._1};
    }
    .ant-pagination-item {
      border: transparent;
      font-size: 14px;
      color: ${(props) => props.theme.text._2};
      font-weight: 500;
      a {
        color: ${(props) => props.theme.text._2};
      }
    }
    .ant-pagination-item-link {
      border: transparent;
    }
    .ant-pagination-options {
      margin-left: 8px;
    }
    .ant-pagination-options-quick-jumper {
      color: ${(props) => props.theme.text._3};
      font-weight: 400;
    }
    .ant-pagination-options-quick-jumper input {
      width: 40px;
      height: 32px;
      border: transparent;
      border-radius: 8px;
      background-color: #f0f5f5;
    }
    .ant-pagination-item-active {
      border: none;
      border-radius: 8px;
      background-color: ${(props) => props.theme.brand._5};
      a {
        color: ${(props) => props.theme.brand._1};
      }
    }
    .ant-pagination-item:hover {
      a {
        color: ${(props) => props.theme.brand._1};
      }
    }
    .ant-pagination-prev:hover .ant-pagination-item-link {
      color: ${(props) => props.theme.brand._1};
    }
    .ant-pagination-next:hover .ant-pagination-item-link {
      color: ${(props) => props.theme.brand._1};
    }

    .ant-btn-link {
      color: ${(props) => props.theme.brand._1};
    }
    .ant-pagination-total-text {
      font-size: 14px;
      font-weight: 400;
      color: ${(props) => props.theme.text._1};
    }
  }
`
